import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { StepModel } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { DownGraphIcon, UpGraphIcon } from '@web/assets/svgs/launchpool-page';

import { Steps } from '.';

interface Props {
  stakingCurrency: string;
  onClose: () => void;
  setModalStep: (step: StepModel<Steps>) => void;
}

const HedgingNoticeForm = ({ stakingCurrency, onClose, setModalStep }: Props) => {
  const { user } = useAuth();

  const [isSkipHedgingNotice, setIsSkipHedgingNotice] = useLocalStorage({
    key: `flipster.configs.v1.${user?.userId}.skipHedgingNoticeConfirm`,
    defaultValue: false,
  });

  const onCloseHedgingNotice = () => {
    setIsSkipHedgingNotice(true);
    onClose();
  };

  const onClickHedging = () => {
    if (!isSkipHedgingNotice) {
      segment.track(StakeEvent.StakeHedgeProceedClick, { currency: stakingCurrency });
    }
    setIsSkipHedgingNotice(true);
    setModalStep({ step: 'hedging', context: undefined });
  };

  return (
    <section>
      <Modal.Content>
        <div
          className={flex({
            alignItems: 'center',
            textAlign: 'center',
            whiteSpace: 'pre-line',
            mb: 16,
            p: 16,
            bgColor: 'background.default.level-2',
            rounded: 8,
            color: 'content.default.level-4',
          })}
        >
          <div className={css({ flex: 1 })}>
            <DownGraphIcon />
            <p className={css({ textStyle: 'Text/L/Strong', mt: 8, mb: 2 })}>Market price drops</p>
            <p className={css({ textStyle: 'Text/M/Regular' })}>{'Staked asset\nvalue drops'}</p>
          </div>
          <ArrowRightIcon className={css({ color: 'content.default.level-1' })} />
          <div className={css({ flex: 1 })}>
            <UpGraphIcon />
            <p className={css({ textStyle: 'Text/L/Strong', mt: 8, mb: 2 })}>Hedging</p>
            <p className={css({ textStyle: 'Text/M/Regular' })}>{'Profit with\nshort positions'}</p>
          </div>
        </div>
        <p className={sectionDescription}>
          If the market price drops, the value of your staked assets may decrease.{' '}
          <strong className={sectionStrong}>Hedging</strong> is a strategy that allows you to set a{' '}
          <strong className={sectionStrong}>short position (a strategy that profits when prices fall)</strong> to make
          gains during price drops.
        </p>
        <p className={sectionDescription}>
          Keep earning rewards from staking, and use hedging to reduce potential losses when prices decline!
        </p>
      </Modal.Content>
      <Modal.Footer>
        {isSkipHedgingNotice && (
          <Button as='button' hierarchy='primary' size='large' label='Close' onClick={onCloseHedgingNotice} />
        )}
        {!isSkipHedgingNotice && (
          <>
            <Button
              as='button'
              hierarchy='secondary'
              size='large'
              color='neutral'
              label='Dismiss'
              onClick={onCloseHedgingNotice}
            />
            <Button as='button' hierarchy='primary' size='large' label='Proceed hedging' onClick={onClickHedging} />
          </>
        )}
      </Modal.Footer>
    </section>
  );
};

export default HedgingNoticeForm;

const sectionDescription = css({
  textStyle: 'Text/L/Regular',
  color: 'content.default.level-3',
  '& + &': {
    mt: 16,
  },
});

const sectionStrong = css({
  textStyle: 'Text/L/Strong',
  color: 'content.default.level-4',
});
